import axios from "axios";
import { renewToken } from "./auth";
import {
  startGetAdvertisersByAgency,
  startGetBusinessNameByAdvertiserIDs,
  startGetSuperAdminAgencyGroups,
} from "./user";
import { getCloudFunctionUrl } from "../utils/cloudFunctions";
import { LaunchError } from "../utils/errors";
import { SUPER_ADMIN_ROLE } from "../utils/constants";
import {
  buildMcpOAuthLaunchErrorArgs,
  requestMcpEndpoint,
} from "./mcpOAuthRequest";
import {
  MCP_LIST_CONNECTIONS_FUNCTION,
  MCP_OAUTH_CONSENT_FUNCTION,
  MCP_OAUTH_REGISTER_FUNCTION,
  MCP_REVOKE_CONNECTION_FUNCTION,
  buildMcpConsentPayload,
  isApprovedConsentRedirect,
  sanitizeMcpConnection,
  sortMcpConnectionsNewestFirst,
} from "../utils/mcpOAuth";

const functionUrl = (name) => `${getCloudFunctionUrl(name)}/${name}`;

const normalizeAdvertiserIDs = (raw) => {
  if (Array.isArray(raw)) {
    return raw.filter((id) => typeof id === "string" && id);
  }
  if (raw && typeof raw === "object") return Object.keys(raw);
  return [];
};

const uniqueAdvertiserIDs = (ids) => {
  const uniqueIDs = [];
  const seen = new Set();
  for (const id of ids) {
    if (typeof id !== "string" || !id || seen.has(id)) continue;
    seen.add(id);
    uniqueIDs.push(id);
  }
  return uniqueIDs;
};

const callMcpEndpoint = ({
  dispatch,
  method,
  url,
  body,
  authToken,
  functionName,
}) =>
  requestMcpEndpoint({
    dispatch,
    launchError: LaunchError,
    axiosClient: axios,
    method,
    url,
    body,
    authToken,
    functionName,
  });

const guardMcpAction = (functionName, action) => async (dispatch, getState) => {
  try {
    return await action(dispatch, getState);
  } catch {
    const { error, showAlert, ignoreAlertByErrors, sentryMeta } =
      buildMcpOAuthLaunchErrorArgs({ status: null, functionName });
    await dispatch(
      LaunchError(error, showAlert, ignoreAlertByErrors, sentryMeta)
    );
    return { ok: false, errorKind: "unexpected" };
  }
};

export const startGetMcpOAuthClient =
  ({ clientID }) =>
  (dispatch) => {
    const url = `${functionUrl(MCP_OAUTH_REGISTER_FUNCTION)}?client_id=${encodeURIComponent(
      clientID
    )}`;

    return callMcpEndpoint({
      dispatch,
      method: "get",
      url,
      functionName: "startGetMcpOAuthClient",
    });
  };

export const startConsentMcpOAuth = ({
  clientID,
  redirectUri,
  codeChallenge,
  state,
  resource,
  advertiserID,
}) =>
  guardMcpAction("startConsentMcpOAuth", async (dispatch) => {
    const token = await dispatch(renewToken());
    if (!token) return { ok: false, errorKind: "unexpected" };

    const result = await callMcpEndpoint({
      dispatch,
      method: "post",
      url: functionUrl(MCP_OAUTH_CONSENT_FUNCTION),
      authToken: token,
      functionName: "startConsentMcpOAuth",
      body: buildMcpConsentPayload({
        clientId: clientID,
        redirectUri,
        codeChallenge,
        state,
        resource,
        advertiserID,
      }),
    });

    if (!result.ok) return result;

    const redirectUrl = result.data?.redirectUrl;
    if (
      typeof redirectUrl !== "string" ||
      !isApprovedConsentRedirect({ redirectUri, redirectUrl })
    ) {
      const { error, showAlert, ignoreAlertByErrors, sentryMeta } =
        buildMcpOAuthLaunchErrorArgs({
          status: result.status ?? null,
          functionName: "startConsentMcpOAuth",
        });
      await dispatch(
        LaunchError(error, showAlert, ignoreAlertByErrors, sentryMeta)
      );
      return { ok: false, errorKind: "unexpected" };
    }

    return { ok: true, data: { redirectUrl } };
  });

export const startListMcpConnections = ({ advertiserID }) =>
  guardMcpAction("startListMcpConnections", async (dispatch) => {
    const token = await dispatch(renewToken());
    if (!token) return { ok: false, errorKind: "unexpected" };

    const result = await callMcpEndpoint({
      dispatch,
      method: "post",
      url: functionUrl(MCP_LIST_CONNECTIONS_FUNCTION),
      authToken: token,
      functionName: "startListMcpConnections",
      body: { advertiserID },
    });

    if (!result.ok) return result;

    const connections = sortMcpConnectionsNewestFirst(
      (Array.isArray(result.data) ? result.data : []).map(sanitizeMcpConnection)
    ).filter((connection) => connection.keyID);

    return { ok: true, data: connections };
  });

export const startRevokeMcpConnection = ({ keyID, advertiserID }) =>
  guardMcpAction("startRevokeMcpConnection", async (dispatch) => {
    const token = await dispatch(renewToken());
    if (!token) return { ok: false, errorKind: "unexpected" };

    return callMcpEndpoint({
      dispatch,
      method: "post",
      url: functionUrl(MCP_REVOKE_CONNECTION_FUNCTION),
      authToken: token,
      functionName: "startRevokeMcpConnection",
      body: { keyID, advertiserID },
    });
  });

export const startListManageableAdvertisers =
  () => async (dispatch, getState) => {
    try {
      const authUser = getState()?.auth?.user;
      const currentAdvertiserID = authUser?.advertiserID || "";
      const currentName = getState()?.user?.businessName || "";
      const advertisersAvailable = Array.isArray(authUser?.advertisersAvailable)
        ? authUser.advertisersAvailable
        : [];
      const agenciesAvailable = Array.isArray(authUser?.agenciesAvailable)
        ? authUser.agenciesAvailable
        : [];
      const isSuperAdminMultiAgency =
        authUser?.role === SUPER_ADMIN_ROLE && agenciesAvailable.length > 0;

      let ids = [];
      if (isSuperAdminMultiAgency) {
        const groups = await dispatch(
          startGetSuperAdminAgencyGroups(agenciesAvailable)
        );
        ids = (groups?.data || []).flatMap(
          (group) => group?.advertiserIDs || []
        );
      } else {
        const response = await dispatch(startGetAdvertisersByAgency());
        const fromAgency = normalizeAdvertiserIDs(response?.data);
        ids =
          advertisersAvailable.length > 0 ? advertisersAvailable : fromAgency;
      }

      if (currentAdvertiserID && !ids.includes(currentAdvertiserID)) {
        ids = [currentAdvertiserID, ...ids];
      }

      const uniqueIDs = uniqueAdvertiserIDs(ids);
      const names = uniqueIDs.length
        ? await dispatch(
            startGetBusinessNameByAdvertiserIDs({ advertiserIDs: uniqueIDs })
          )
        : {};
      const nameByID = names && typeof names === "object" ? names : {};

      return {
        ok: true,
        advertisers: uniqueIDs.map((id) => ({
          id,
          name:
            (typeof nameByID[id] === "string" && nameByID[id]) ||
            (id === currentAdvertiserID ? currentName : "") ||
            "",
        })),
      };
    } catch {
      const {
        error: safeError,
        showAlert,
        ignoreAlertByErrors,
        sentryMeta,
      } = buildMcpOAuthLaunchErrorArgs({
        status: null,
        functionName: "startListManageableAdvertisers",
      });
      dispatch(
        LaunchError(safeError, showAlert, ignoreAlertByErrors, sentryMeta)
      );
      return { ok: false, advertisers: [] };
    }
  };
